import { useState, useEffect, useCallback } from 'react';
import { useIntegrationApp } from '@membranehq/react';
import { useTheme } from 'components/core/theme-provider';
import { useCreateIntegration } from 'hooks/use-create-integration';
import { useDeleteIntegration } from 'hooks/use-delete-integration';
import { useSyncIntegration } from 'hooks/use-sync-integration';
import { useCheckIntegrationLimit } from 'hooks/use-check-integration-limit';
import { useToast } from 'hooks/use-toast';
import { Integration } from 'services/integration-service';
import { Button } from 'components/ui/button';

// Define types for generic connections
interface GenericConnection {
    id: string;
    name?: string;
    externalId?: string;
    status?: string;
    createdAt?: string;
}

interface GenericConnectorProps {
    existingIntegration?: Integration;
    integrationType: string;
    integrationName: string;
    allowMultipleConnections?: boolean;
    scope: 'user' | 'organization';
}

export function GenericConnector({
    existingIntegration,
    integrationType,
    integrationName,
    allowMultipleConnections = true,
    scope
}: GenericConnectorProps) {
    const [isConnecting, setIsConnecting] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState<string | null>(null);
    const [connections, setConnections] = useState<GenericConnection[]>([]);
    const [connectionStatus, setConnectionStatus] = useState<{ [key: string]: 'connecting' | 'connected' | 'disconnected' | 'error' }>({});
    const integrationApp = useIntegrationApp();
    const { mutate: createIntegrationMutation } = useCreateIntegration();
    const { mutate: deleteIntegrationMutation } = useDeleteIntegration();
    const { mutate: syncIntegrationMutation } = useSyncIntegration();
    const { refetch: checkIntegrationLimit } = useCheckIntegrationLimit();
    const { toast } = useToast();
    // Theme is available for future theme-specific logic
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { theme } = useTheme();

    const handleConnect = async () => {
        try {
            setIsConnecting(true);

            // Check integration limit before creating connection
            try {
                const limitResult = await checkIntegrationLimit();

                if (!limitResult.data?.allowed) {
                    console.error('Integration limit reached or not available in your plan');
                    toast({
                        variant: "destructive",
                        title: "Integration Limit Reached",
                        description: limitResult.data?.message || "Integration access not available in your plan. Please upgrade your plan to add more integrations.",
                    });
                    return;
                }
            } catch (limitError: any) {
                // Handle 403 error specifically
                if (limitError?.status === 403 || limitError?.response?.status === 403) {
                    const errorMessage = limitError?.response?.data?.detail || limitError?.detail || "Integration access not available in your plan";
                    console.error('403 - Integration access denied:', errorMessage);
                    toast({
                        variant: "destructive",
                        title: "Access Denied",
                        description: errorMessage,
                    });
                    return;
                } else {
                    // Handle other limit check errors
                    console.error('Failed to check integration limits:', limitError);
                    toast({
                        variant: "destructive",
                        title: "Unable to Check Limits",
                        description: "Failed to verify integration limits. Please try again.",
                    });
                    return;
                }
            }

            // eslint-disable-next-line no-console
            console.log('Integration limit check passed, proceeding with connection');

            // Open connection UI for the specified integration
            const connection: any = await integrationApp
                .integration(integrationType)
                .openNewConnection({
                    allowMultipleConnections
                });

            if (connection) {
                setConnections(prev => [...prev, connection]);
                setConnectionStatus(prev => ({ ...prev, [connection.id]: 'connecting' }));
                // eslint-disable-next-line no-console
                console.log(`${integrationName} connected successfully:`, connection);

                // Create integration after successful connection
                createIntegrationMutation({
                    connection_id: connection.id,
                    scope: scope,
                    integration_type: integrationType
                }, {
                    onSuccess: () => {
                        // After successful integration creation, sync the integration
                        syncIntegrationMutation(connection.id, {
                            onSuccess: () => {
                                setConnectionStatus(prev => ({ ...prev, [connection.id]: 'connected' }));
                                // eslint-disable-next-line no-console
                                console.log('Integration synced successfully');
                            },
                            onError: (error) => {
                                console.error('Failed to sync integration:', error);
                                // If sync fails, delete the integration and show disconnected
                                deleteIntegrationMutation(connection.id, {
                                    onSuccess: () => {
                                        setConnectionStatus(prev => ({ ...prev, [connection.id]: 'disconnected' }));
                                        setConnections(prev => prev.filter(conn => conn.id !== connection.id));
                                        // eslint-disable-next-line no-console
                                        console.log('Integration deleted after sync failure');
                                    },
                                    onError: (deleteError) => {
                                        console.error('Failed to delete integration after sync failure:', deleteError);
                                        setConnectionStatus(prev => ({ ...prev, [connection.id]: 'error' }));
                                    }
                                });
                            }
                        });
                    },
                    onError: (error) => {
                        console.error('Failed to create integration:', error);
                        setConnectionStatus(prev => ({ ...prev, [connection.id]: 'error' }));
                    }
                });

                // Connection successful - could add toast notification here
            }
        } catch (error) {
            console.error(`Failed to connect to ${integrationName}:`, error);
            toast({
                variant: "destructive",
                title: "Connection Failed",
                description: `Failed to connect to ${integrationName}. Please try again.`,
            });
        } finally {
            setIsConnecting(false);
        }
    };

    const handleDisconnect = async (connectionId: string) => {
        try {
            setIsDisconnecting(connectionId);

            // Then, delete the integration from our backend (store will be updated automatically)
            await new Promise((resolve, reject) => {
                deleteIntegrationMutation(connectionId, {
                    onSuccess: () => {
                        // eslint-disable-next-line no-console
                        console.log('Integration deleted successfully');
                        resolve(undefined);
                    },
                    onError: (error) => {
                        console.error('Failed to delete integration:', error);
                        reject(error);
                    }
                });
            });

            setConnections(prev => prev.filter(conn => conn.id !== connectionId));

            toast({
                variant: "success",
                title: "Disconnected",
                description: `Disconnected ${integrationName} successfully.`,
            });
        } catch (error) {
            console.error('Error disconnecting:', error);
            toast({
                variant: "destructive",
                title: "Error Disconnecting",
                description: `Failed to disconnect ${integrationName}. Please try again.`,
            });
        } finally {
            setIsDisconnecting(null);
        }
    };

    const loadExistingConnections = useCallback(async () => {
        try {
            // For now, start with empty connections array
            // This would be replaced with actual API call once the integration method is confirmed
            setConnections([]);
        } catch (error) {
            // Error loading connections - could add error toast notification here
        }
    }, []);

    // Load existing connections on component mount
    useEffect(() => {
        loadExistingConnections();
    }, [loadExistingConnections]);

    // Show disconnect button if there's an existing integration
    if (existingIntegration) {
        const isCurrentlyDisconnecting = isDisconnecting === existingIntegration.connection_id;

        return (
            <div className="flex items-center gap-2">
                <button
                    onClick={() => handleDisconnect(existingIntegration.connection_id)}
                    disabled={isCurrentlyDisconnecting}
                    className="bg-destructive hover:bg-destructive/90 disabled:bg-destructive/50 disabled:cursor-not-allowed text-destructive-foreground px-3 sm:px-4 py-2 sm:py-1.5 rounded-md text-[13px] sm:text-[14px] font-semibold leading-[20px] sm:leading-[24px] transition-colors min-w-[100px] sm:min-w-auto"
                >
                    {isCurrentlyDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                </button>
            </div>
        );
    }

    return (
        <>
            {connections.length === 0 ? (
                <Button
                    onClick={handleConnect}
                    disabled={isConnecting}
                    className="bg-primary hover:bg-primary-600 disabled:bg-primary-500 disabled:cursor-not-allowed px-4 sm:px-6 py-2 sm:py-1.5 rounded-md text-[13px] sm:text-[14px] leading-[20px] sm:leading-[24px] min-w-[120px] sm:min-w-auto"
                >
                    {isConnecting ? (
                        <>
                            Connecting...
                        </>
                    ) : (
                        <>
                            Connect
                        </>
                    )}
                </Button>
            ) : (
                connections.map((connection) => {
                    const isCurrentlyDisconnecting = isDisconnecting === connection.id;
                    const status = connectionStatus[connection.id];

                    if (status === 'connected') {
                        return (
                            <div key={connection.id} className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                                <span className="text-success-green text-[13px] sm:text-[14px] font-semibold">Connected</span>
                                <button
                                    onClick={() => handleDisconnect(connection.id)}
                                    disabled={isCurrentlyDisconnecting}
                                    className="bg-destructive hover:bg-destructive/90 disabled:bg-destructive/50 disabled:cursor-not-allowed text-destructive-foreground px-3 sm:px-4 py-2 sm:py-1.5 rounded-md text-[13px] sm:text-[14px] font-semibold leading-[20px] sm:leading-[24px] transition-colors min-w-[100px] sm:min-w-auto"
                                >
                                    {isCurrentlyDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                                </button>
                            </div>
                        );
                    }

                    /* if (status === 'connecting') {
                        return (
                            <div key={connection.id} className="flex items-center gap-2">
                                <span className="text-blue-600 text-[14px] font-semibold">Connecting...</span>
                            </div>
                        );
                    } */

                    if (status === 'error') {
                        return (
                            <div key={connection.id} className="flex items-center gap-2">
                                <span className="text-destructive text-[13px] sm:text-[14px] font-semibold">Connection Failed</span>
                            </div>
                        );
                    }

                    return (
                        <button
                            key={connection.id}
                            onClick={() => handleDisconnect(connection.id)}
                            disabled={isCurrentlyDisconnecting}
                            className="bg-destructive hover:bg-destructive/90 disabled:bg-destructive/50 disabled:cursor-not-allowed text-destructive-foreground px-3 sm:px-4 py-2 sm:py-1.5 rounded-md text-[13px] sm:text-[14px] font-semibold leading-[20px] sm:leading-[24px] transition-colors min-w-[100px] sm:min-w-auto"
                        >
                            {isCurrentlyDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                        </button>
                    );
                })
            )}
        </>
    );
}
